/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'
import moment from 'moment'
import { isEqual } from 'lodash'

import {
  Table,
  SvgIcon,
  Spinner,
  PageNavigationButtons,
  CenteredContainerDisplay,
  PayrollCycleName,
  PayrollCycleFrequency,
  DeductionPaymentsTotal
} from '../'

import {
  PageNavigationControl,
  decodeBase64,
  saveCursor,
  getPageCursor
} from '../../utils'

class PayrollRuns extends React.PureComponent {
  static propTypes = {
    payrollruns: PropTypes.object,
    employerId: PropTypes.string,
    clientId: PropTypes.string,
    payrollCycle: PropTypes.string,
    paidOnFrom: PropTypes.string,
    paidOnTo: PropTypes.string,
    status: PropTypes.string,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func.isRequired,
  }

  static defaultProps = {
    recordsPerPage: 15
  }

  state = {
    pageNav: new PageNavigationControl(),
    filterChanged: false
  }

  UNSAFE_componentWillReceiveProps({ loading, employerId, payrollCycle, paidOnFrom, paidOnTo, status, orderBy, payrollruns }) {
    if ((this.props.employerId !== employerId ||
      this.props.payrollCycle !== payrollCycle ||
      this.props.paidOnFrom !== paidOnFrom ||
      this.props.paidOnTo !== paidOnTo ||
      this.props.status !== status ||
      this.props.orderBy !== orderBy) &&
      !sessionStorage.getItem('payrollrunListPageState')
    ) {
      let previousFilters = JSON.parse(sessionStorage.getItem('payrollrunListFilters'))
      if (previousFilters && (
        previousFilters.employerId === employerId ||
        previousFilters.payrollCycle === payrollCycle ||
        previousFilters.paidOnFrom === paidOnFrom ||
        previousFilters.paidOnTo === paidOnTo ||
        previousFilters.status === status ||
        previousFilters.orderBy === orderBy
      )) {
        sessionStorage.removeItem('payrollrunListFilters')
      } else {
        this.state.pageNav.reset()
        this.setState({ filterChanged: true }, () => this.props.refetch())
        sessionStorage.removeItem('payrollrunListFilters')
      }
    } else {
      if (!loading) {
        if (this.state.filterChanged) {
          this.setState({ filterChanged: false })
          this.state.pageNav.setPageStates(null, payrollruns)
        }
        else if (payrollruns) {
          let payrollrunListPageState = JSON.parse(sessionStorage.getItem('payrollrunListPageState'))
          if (payrollrunListPageState) {
            this.state.pageNav.setPageStates(null, null, payrollrunListPageState)
            sessionStorage.removeItem('payrollrunListPageState')
            this.props.refetch({ cursor: payrollrunListPageState.cursor })
          } else if (!isEqual(this.props.payrollruns, payrollruns)) {
            this.state.pageNav.setPageStates(this.props.payrollruns, payrollruns)
          }
        }
      }
    }
  }

  render() {
    let { loading, error, payrollruns, orderBy, changeSort, loadNextPayrollRuns, loadPreviousPayrollRuns, history, recordsPerPage } = this.props
    if (loading) {
      return (
        <CenteredContainerDisplay
          content={<Spinner className='slds-is-relative' />}  // TODO: loading state stencil instead
          yAdjustPercentage={-15}
        />
      )
    }

    if (error) {
      return <div>Failed to load payroll runs</div>  // TODO: improve error response?
    }

    if (payrollruns.edges.length === 0) {
      return (
        <div className='slds-m-around_large'>
          <SvgIcon
            variant='utility'
            symbol='info'
            colour='light'
            size='small'
            className='slds-m-right_medium'
          />
          No payroll run records to display
        </div>
      )
    }

    let pageNav = this.state.pageNav
    let tableColumns = [ 'Paid on', 'Initiate on', 'Payroll cycle name', 'Frequency', 'Amount', 'Released', 'Approved (amount)', 'Approved (payment)' ]

    let startCursor = payrollruns.pageInfo.startCursor
    let totalCount = payrollruns.totalCount

    return (
      <div className='slds-scrollable_x'>
        <Table
          columns={tableColumns}
          rows={
            payrollruns.edges.map(({ node: payrollrun }, index) => (
              <PayrollRunDetailsRow
                id={payrollrun.id}
                workflowRunDeductingId={payrollrun.workflowrunDeducting
                  ? payrollrun.workflowrunDeducting.id
                  : null
                }
                payrollDate={payrollrun.paidOn}
                createdAt={payrollrun.createdAt}
                initiateOn={payrollrun.initiateOn}
                payrollCycleId={payrollrun.payrollcycle.id}
                amount={payrollrun.amount}
                internalApprovalAt={payrollrun.internalApprovalAt}
                amountApprovalAt={payrollrun.amountApprovalAt}
                paymentApprovalAt={payrollrun.paymentApprovalAt}
                onClick={() => {
                  sessionStorage.setItem('payrollrunListPageState', JSON.stringify({
                    firstPage: this.state.pageNav.firstPage,
                    lastPage: this.state.pageNav.lastPage,
                    currentPage: this.state.pageNav.currentPage,
                    pages: this.state.pageNav.pages,
                    cursor: saveCursor(payrollruns.pageInfo.startCursor)
                  }))
                  sessionStorage.setItem('payrollrunListFilters', JSON.stringify({
                    orderBy: orderBy
                  }))
                  history.push(`/os/operations/general/payrollruns/${payrollrun.id}?tabView=summary`)
                }}
                key={`payrollrunRow_${index}`}
              />
            ))
          }
          objectRows={true}
          orderBy={orderBy}
          changeSort={changeSort}
          querySortableColumns={{
            'Paid on': {
              orderByName: 'paid_on'
            }
          }}
        />
        <PageNavigationButtons
          onNextClick={() => pageNav.nextPage(loadNextPayrollRuns)}
          onPreviousClick={() => pageNav.previousPage(loadPreviousPayrollRuns)}
          previousDisabled={pageNav.firstPage}
          nextDisabled={pageNav.lastPage}
          startCursor={startCursor}
          totalCount={totalCount}
          resultsPerPage={recordsPerPage}
          resultsText='payroll runs'
          onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, recordsPerPage) })}
          className='slds-m-top_small slds-clearfix slds-float_right'
        />
      </div>
    )
  }
}

const PayrollRunDetailsRow = ({ id, workflowRunDeductingId, payrollDate, createdAt, initiateOn, payrollCycleId, amount, internalApprovalAt, amountApprovalAt, paymentApprovalAt, onClick }) => {
  return (
    <tr className='case-table-row' onClick={onClick} key={`payrollRunDetails_${id}`}>
      <th scope='row' data-label='Paid on'>
        <div className='slds-truncate' title='Client name'>
          {payrollDate}
        </div>
      </th>
      <td data-label='Initiate on'>
        <div className='slds-truncate' title='Scheduled'>
          {initiateOn ? moment(initiateOn).format('YYYY-MM-DD') : '-'}
        </div>
      </td>
      <td data-label='Payroll cycle name'>
        <div className='slds-truncate' title='Payroll cycle name'>
          <PayrollCycleName payrollCycleId={payrollCycleId} />
        </div>
      </td>
      <td data-label='Payroll cycle cycle'>
        <div className='slds-truncate' title='Payroll cycle cycle'>
          <PayrollCycleFrequency payrollCycle={payrollCycleId} />
        </div>
      </td>
      <td data-label='Amount'>
        <div className='slds-truncate' title='Amount'>
          {workflowRunDeductingId
            ? <DeductionPaymentsTotal workflowRun={workflowRunDeductingId} />
            : '-'
          }
        </div>
      </td>
      <td data-label='Approved'>
        <div className='slds-truncate' title='Approved'>
          {internalApprovalAt
            ? moment(internalApprovalAt).format('YYYY-MM-DD HH:mm')
            : ''
          }
        </div>
      </td>
      <td data-label='Amount approved'>
        <div className='slds-truncate' title='Amount approved'>
          {amountApprovalAt
            ? moment(amountApprovalAt).format('YYYY-MM-DD HH:mm')
            : ''
          }
        </div>
      </td>
      <td data-label='Payment approved'>
        <div className='slds-truncate' title='Payment approved'>
          {paymentApprovalAt
            ? moment(paymentApprovalAt).format('YYYY-MM-DD HH:mm')
            : ''
          }
        </div>
      </td>
    </tr>
  )
}

const getPayrollRuns = gql`
  query($employer: UUID, $payrollCycle: UUID, $identity: UUID, $paidOnFrom: Date, $paidOnTo: Date, $status: String, $recordsPerPage: Int!, $orderBy: String!, $cursor: String) {
    payrollruns(payrollcycle_Employer_Id: $employer, payrollcycle_Id: $payrollCycle, payrollcycle_Clients_Identity_Id: $identity, paidOn_Gte: $paidOnFrom, paidOn_Lte: $paidOnTo, status: $status, orderBy: $orderBy, first: $recordsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          paidOn
          initiateOn
          payrollcycle {
            id
            name
          }
          batchPayment {
            id
            status
          }
          workflowrunDeducting {
            id
            parameters
            status
          }
          workflowrunReceipting {
            id
            status
          }
          internalApprover {
            id
            firstName
          }
          internalApprovalAt
          amountApprover {
            id
            firstName
          }
          amountApprovalAt
          paymentApprover {
            id
            firstName
          }
          paymentApprovalAt
          paymentsBreakdown
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousPayrollRuns = gql`
  query($employer: UUID, $payrollCycle: UUID, $identity: UUID, $paidOnFrom: Date, $paidOnTo: Date, $status: String, $recordsPerPage: Int!, $orderBy: String!, $cursor: String) {
    payrollruns(payrollcycle_Employer_Id: $employer, payrollcycle_Id: $payrollCycle, payrollcycle_Clients_Identity_Id: $identity, paidOn_Gte: $paidOnFrom, paidOn_Lte: $paidOnTo, status: $status, orderBy: $orderBy, last: $recordsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          paidOn
          initiateOn
          payrollcycle {
            id
            name
          }
          batchPayment {
            id
            status
          }
          workflowrunDeducting {
            id
            parameters
            status
          }
          workflowrunReceipting {
            id
            status
          }
          internalApprover {
            id
            firstName
          }
          internalApprovalAt
          amountApprover {
            id
            firstName
          }
          amountApprovalAt
          paymentApprover {
            id
            firstName
          }
          paymentApprovalAt
          paymentsBreakdown
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData = graphql(getPayrollRuns, {
    options: ({ employerId, identityId, payrollCycle, paidOnFrom, paidOnTo, status, recordsPerPage = 15, orderBy, cursor }) => ({
      variables: {
        employer: decodeBase64(employerId),
        identity: identityId
          ? decodeBase64(identityId)
          : undefined,
        payrollCycle: decodeBase64(payrollCycle),
        paidOnFrom,
        paidOnTo,
        status,
        orderBy,
        recordsPerPage: recordsPerPage || 15,
        cursor
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps: { employerId, identityId, payrollCycle, paidOnFrom, paidOnTo, status, recordsPerPage = 15, orderBy }, data: { loading, error, refetch, payrollruns, fetchMore } }) => ({
      loading,
      error,
      refetch,
      payrollruns,
      loadNextPayrollRuns: () => {
        return fetchMore({
          query: getPayrollRuns,
          variables: {
            employer: decodeBase64(employerId),
            identity: identityId
              ? decodeBase64(identityId)
              : undefined,
            payrollCycle: decodeBase64(payrollCycle),
            paidOnFrom,
            paidOnTo,
            status,
            orderBy,
            cursor: payrollruns.pageInfo.endCursor,
            recordsPerPage,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.payrollruns.edges
            const pageInfo = fetchMoreResult.payrollruns.pageInfo

            return {
              payrollruns: {
                __typename: previousResult.payrollruns.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      loadPreviousPayrollRuns: () => {
        return fetchMore({
          query: getPreviousPayrollRuns,
          variables: {
            employer: decodeBase64(employerId),
            identity: identityId
              ? decodeBase64(identityId)
              : undefined,
            payrollCycle: decodeBase64(payrollCycle),
            paidOnFrom,
            paidOnTo,
            status,
            orderBy,
            cursor: payrollruns.pageInfo.startCursor,
            recordsPerPage,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.payrollruns.edges
            const pageInfo = fetchMoreResult.payrollruns.pageInfo

            return {
              payrollruns: {
                __typename: previousResult.payrollruns.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })

export default compose(
  withData,
  withRouter
)(PayrollRuns)



// WEBPACK FOOTER //
// ./src/components/payroll-runs/index.js